import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getTenantSlug, type ImpersonationUser } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { superAdminUrl } from '../utils/superAdminUrl';
import { landingPathForRole, storeImpersonationSession } from '../utils/impersonationSession';
import './ImpersonationSwitcher.css';
import { translateError } from '../utils/errorMessages';

/** True while this browser holds an impersonation session for this origin. */
const isImpersonating = (): boolean => {
  try {
    return localStorage.getItem('impersonating') === 'true';
  } catch {
    return false;
  }
};

/**
 * Second dropdown beside the user menu, for support work: switch to any user of
 * the tenant you are looking at, or leave and go back to the super-admin app.
 *
 * Shown while impersonating, and also to a real super admin browsing a tenant —
 * so a session can be started from inside the tenant rather than by going back
 * to the apex Users page. Visibility keys off the impersonation marker and
 * `isSuperAdmin`, never off a permission, so it cannot widen anyone's access;
 * the endpoints behind it re-check that on every call.
 */
const ImpersonationSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const { user, isSuperAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<ImpersonationUser[] | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const impersonating = isImpersonating();
  const hasTenant = getTenantSlug() !== null;
  const visible = !!user && hasTenant && (impersonating || isSuperAdmin);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, []);

  // Fetched on first open, not on mount: this renders on every page for a super
  // admin, and most of those page loads never open it.
  useEffect(() => {
    if (!isOpen || users !== null) return;
    let cancelled = false;
    api.impersonation.users()
      .then(({ data }) => { if (!cancelled) setUsers(data); })
      .catch(() => { if (!cancelled) setError(t('impersonation.loadFailed')); });
    return () => { cancelled = true; };
  }, [isOpen, users, t]);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!users) return [];
    const needle = query.trim().toLocaleLowerCase('cs');
    if (!needle) return users;
    return users.filter((u) => (
      (u.name || '').toLocaleLowerCase('cs').includes(needle)
      || (u.email || '').toLocaleLowerCase('cs').includes(needle)
      || (u.role.name || '').toLocaleLowerCase('cs').includes(needle)
    ));
  }, [users, query]);

  const switchTo = useCallback(async (target: ImpersonationUser) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.impersonation.switch(target._id);
      storeImpersonationSession(data);
      // Full navigation so AuthContext rehydrates from the new session.
      window.location.href = landingPathForRole(data.role);
    } catch (err: any) {
      setError(translateError(err, t, 'impersonation.switchFailed'));
      setBusy(false);
    }
  }, [t]);

  const returnToSuperAdmin = useCallback(async () => {
    setBusy(true);
    // Best-effort audit trail; leaving must not depend on it.
    try {
      await api.impersonation.stop();
    } catch {
      // ignored
    }
    window.location.href = superAdminUrl();
  }, []);

  if (!visible) return null;

  const label = impersonating
    ? (user?.name || user?.email || t('impersonation.switchUser'))
    : t('impersonation.switchUser');

  return (
    <div className="impersonation-switcher" ref={dropdownRef}>
      <button
        type="button"
        className="impersonation-switcher__toggle"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title={t('impersonation.switchUser')}
      >
        <i className="fas fa-user-secret" aria-hidden="true" />
        <span className="impersonation-switcher__label">{label}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {isOpen && (
        <div className="impersonation-switcher__dropdown" role="menu">
          <button
            type="button"
            className="impersonation-switcher__item impersonation-switcher__item--return"
            role="menuitem"
            disabled={busy}
            onClick={returnToSuperAdmin}
          >
            <i className="fas fa-arrow-left" aria-hidden="true" />
            <span>{t('impersonation.returnToSuperAdmin')}</span>
          </button>

          <div className="impersonation-switcher__search">
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('impersonation.searchPlaceholder')}
              aria-label={t('impersonation.searchPlaceholder')}
            />
          </div>

          {error && <p className="impersonation-switcher__error">{error}</p>}

          <div className="impersonation-switcher__list">
            {users === null && !error && (
              <p className="impersonation-switcher__empty">{t('common.loading')}</p>
            )}
            {users !== null && filtered.length === 0 && (
              <p className="impersonation-switcher__empty">{t('impersonation.noMatches')}</p>
            )}
            {filtered.map((candidate) => (
              <button
                type="button"
                key={candidate._id}
                className="impersonation-switcher__item"
                role="menuitem"
                disabled={busy || candidate.isCurrent}
                onClick={() => switchTo(candidate)}
              >
                <span className="impersonation-switcher__name">
                  {candidate.name || candidate.email}
                  {candidate.isCurrent && ` · ${t('impersonation.current')}`}
                </span>
                <span className="impersonation-switcher__meta">
                  {candidate.role.name}
                  {candidate.email && candidate.name ? ` · ${candidate.email}` : ''}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpersonationSwitcher;
